import { useRef } from 'react'

import { Button } from '@/components/ui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { IconStrokes, MenuIconSizes } from './enums'
import { IconProps } from '@/components/icons/AnnotationsIcons'
import { DEFAULT_ANNOTATION_SIZE, DEFAULT_STROKE_COLOR } from './constants'
import { cn } from '@/utils'
import { useDocumentState } from '@/state'
import { AnnotationSize } from '@/services/hooks/template_attributes'

export type Icon = ({
  className,
  id,
  strokeWidth,
  strokeColor,
}: IconProps) => React.JSX.Element
type AnnotationsToolbarMenuItemProps = {
  Icon: Icon
  id: string
  size?: AnnotationSize
  strokeColor?: string
  bubbleValue?: string
  description?: string
}
export const AnnotationsToolbarMenuItem = (
  props: AnnotationsToolbarMenuItemProps,
) => {
  const itemRef = useRef<HTMLDivElement>(null)

  return (
    <div className="relative" ref={itemRef}>
      <AnnotationsToolbarMenuItemContent {...props} />
    </div>
  )
}

const AnnotationsToolbarMenuItemContent = ({
  Icon,
  id,
  size = DEFAULT_ANNOTATION_SIZE,
  strokeColor,
  className,
  bubbleValue,
  description,
}: AnnotationsToolbarMenuItemProps & { className?: string }) => {
  const isAddingElement = useDocumentState((state) => state.isAddingElement)
  const startAddingElement = useDocumentState(
    (state) => state.startAddingElement,
  )
  const selectedPage = useDocumentState((state) => state.currentPageInView)

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            className={cn(
              className,
              'flex items-center justify-center w-8 h-8 p-0.5',
            )}
            variant="outline"
            disabled={isAddingElement}
            data-testid={`annotation-${id}-${size}`}
            onClick={(e) => {
              const attributes = {
                type: id,
                size,
                strokeColor,
              } as any

              if (bubbleValue) {
                attributes.bubbleValue = bubbleValue
              }
              startAddingElement({
                newElementData: {
                  Component: Icon,
                  dataType: 'annotation',
                  attributes,
                  documentPageId: selectedPage as string,
                },
                triggerLocation: { x: e.clientX, y: e.clientY },
              })
            }}
          >
            <Icon
              className={MenuIconSizes[size || ''] ?? MenuIconSizes['medium']}
              strokeWidth={(IconStrokes[size || ''] ?? 0) + 1}
              strokeColor={strokeColor ?? DEFAULT_STROKE_COLOR}
            />
          </Button>
        </TooltipTrigger>
        {description && (
          <TooltipContent className="bg-black text-white">
            {description}
          </TooltipContent>
        )}
      </Tooltip>
    </TooltipProvider>
  )
}

export default AnnotationsToolbarMenuItem
